import React, { useEffect } from "react";
import { AlertCircle, Calendar, Package, Bell, ShoppingBag } from "lucide-react";
import {
  useLazyAbandonedCartByIdQuery,
  useSendReminderMutation,
} from "../../../redux/actions/Admin/abandonedCartAdmin";
import { useAlert } from "../../../components/customer/AlertContext";
import CommonButton from "../../../components/CommonButton";

interface IAbandonedCartDetailsDrawerProps {
  userId: number;
}

export const AbandonedCartDetailsDrawer: React.FC<IAbandonedCartDetailsDrawerProps> = ({ userId }) => {
  const { showConfirm } = useAlert();
  const [fetchCartDetails, { data: cartDetails, isLoading }] = useLazyAbandonedCartByIdQuery();
  const [sendReminder, { isLoading: isSendingReminder }] = useSendReminderMutation();

  useEffect(() => {
    if (userId) {
      fetchCartDetails(userId);
    }
  }, [userId, fetchCartDetails]);

  const handleSendReminder = () => {
    showConfirm({
      type: "success",
      title: "Send Cart Reminder?",
      message: "Send an abandoned cart notification to this customer?",
      confirmText: "Yes, Send",
      showCancel: true,
      onConfirm: async () => {
        try {
          await sendReminder(userId).unwrap();
          showConfirm({
            type: "success",
            title: "Reminder Sent",
            message: "Abandoned cart notification sent successfully.",
            confirmText: "OK",
            showCancel: false,
          });
          
        } catch (err: any) {
          showConfirm({
            type: "error",
            title: "Failed",
            message: err?.data?.Message || "Unable to send reminder.",
            confirmText: "OK",
            showCancel: false,
          });
        }
      },
    });
  };

  if (isLoading) {
    return <p className="text-xs text-iconColor p-8 text-center">Loading cart details...</p>;
  }

  const payload = cartDetails?.data;

  return (
    <div className="flex flex-col text-left space-y-6">
      {/* Customer Header */}
      <div className="pb-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-logoHeading">{payload?.customerName}</h3>
          <p className="text-xs text-iconColor mt-0.5">
            {payload?.email || payload?.phoneNumber || "No contact info available"}
          </p>
          {payload?.lastActivity && (
            <p className="text-[10px] text-slate-400 mt-1">
              Last active: {new Date(payload.lastActivity).toLocaleString()}
            </p>
          )}
        </div>

        <CommonButton
          text={isSendingReminder ? "Sending..." : "Send Reminder"}
          variant="filled"
          icon={<Bell className="w-3.5 h-3.5" />}
          className="bg-mainBrandColor font-bold text-xs text-white px-4 py-2 rounded-xl"
          disabled={isSendingReminder}
          onClick={handleSendReminder}
        />
      </div>

      {/* Cart Summary Header */}
      <div className="bg-[#f9f9f9] border border-accent/20 p-4 rounded-2xl flex justify-between items-center">
        <div>
          <span className="text-[10px] uppercase font-bold text-iconColor block">Total Abandoned Value</span>
          <span className="text-base font-black text-mainBrandColor">
            AED {(payload?.totalValue || 0).toFixed(2)}
          </span>
        </div>
        <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center text-mainBrandColor">
          <ShoppingBag className="w-5 h-5" />
        </div>
      </div>

      {/* Cart Items List */}
      <div className="space-y-6">
        {/* Products Section */}
        {payload?.products && payload.products.length > 0 && (
          <div className="space-y-2.5">
            <h4 className="text-[11px] font-black uppercase text-logoHeading tracking-wider flex items-center gap-2">
              <Package className="w-4 h-4 text-mainBrandColor" /> Products ({payload.products.length})
            </h4>
            <div className="space-y-2">
              {payload.products.map((p) => (
                <div
                  key={p.productVariantId}
                  className="border border-gray-100 bg-[#f9f9f9]/40 p-3.5 rounded-xl flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-3 min-w-0 pr-2">
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt={p.productName} className="w-10 h-10 object-cover rounded-lg border shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h5 className="font-bold text-logoHeading truncate">{p.productName}</h5>
                      <p className="text-iconColor text-[10px] mt-0.5 truncate">
                        Variant: {p.variantName || "Default"} • Qty: {p.quantity} × AED {p.unitPrice}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-logoHeading shrink-0">AED {(p.totalPrice || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pets Section */}
        {payload?.pets && payload.pets.length > 0 && (
          <div className="space-y-2.5">
            <h4 className="text-[11px] font-black uppercase text-logoHeading tracking-wider flex items-center gap-2">
              🐾 Pets ({payload.pets.length})
            </h4>
            <div className="space-y-2">
              {payload.pets.map((pet) => (
                <div
                  key={pet.petId}
                  className="border border-gray-100 bg-[#f9f9f9]/40 p-3.5 rounded-xl flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-3 min-w-0 pr-2">
                    {pet.imageUrl ? (
                      <img src={pet.imageUrl} alt={pet.petName} className="w-10 h-10 object-cover rounded-lg border shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
                        🐾
                      </div>
                    )}
                    <div className="min-w-0">
                      <h5 className="font-bold text-logoHeading truncate">{pet.petName}</h5>
                      <p className="text-iconColor text-[10px] mt-0.5 truncate">
                        Breed: {pet.breed} • Type: {pet.purchaseType}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-amber-700 shrink-0">AED {(pet.amount || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Section */}
        {payload?.services && payload.services.length > 0 && (
          <div className="space-y-2.5">
            <h4 className="text-[11px] font-black uppercase text-logoHeading tracking-wider flex items-center gap-2">
              <Calendar className="w-4 h-4 text-mainBrandColor" /> Services ({payload.services.length})
            </h4>
            <div className="space-y-2">
              {payload.services.map((s) => (
                <div
                  key={s.serviceId}
                  className="border border-gray-100 bg-[#f9f9f9]/40 p-3.5 rounded-xl flex items-center justify-between text-xs"
                >
                  <div className="pr-2 min-w-0">
                    <h5 className="font-bold text-logoHeading truncate">{s.serviceName}</h5>
                    <p className="text-iconColor text-[10px] mt-0.5 truncate">
                      {s.packageName ? `${s.packageName} • ` : ""}
                      {s.bookingDate} ({s.startTime} - {s.endTime})
                    </p>
                  </div>
                  <span className="font-bold text-purple-700 shrink-0">AED {(s.price || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty Fallback */}
        {!payload?.products?.length && !payload?.pets?.length && !payload?.services?.length && (
          <div className="text-center py-12 border border-dashed border-slate-200 bg-[#f9f9f9] rounded-2xl p-6">
            <AlertCircle className="w-6 h-6 text-iconColor mx-auto mb-2" />
            <p className="text-xs text-iconColor font-semibold">This cart is now empty.</p>
          </div>
        )}
      </div>
    </div>
  );
};